import { apiRequest } from '../api/client'
import type {
  ChangePasswordRequest,
  CurrentUser,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  ResendTwoFactorCodeRequest,
  SessionResponse,
  SetPasswordRequest,
  TwoFactorCodeDelivery,
  VerifyTwoFactorRequest,
} from '../api/contracts'

/**
 * Kimlik uçları (`docs/spec/06-api-spec.md §Auth`).
 *
 * Buradaki çağrıların çoğu `anonymous`: istemci bearer token eklemez ve 401'de yenilemeye
 * çalışmaz. Bu önemli — başarısız bir giriş yanlış şifredir; ona karşılık yenileme yapmak,
 * reddedilmiş bir kimlik bilgisini kafa karıştırıcı ikinci bir hataya çevirirdi.
 */
export const authApi = {
  login: (request: LoginRequest) =>
    apiRequest<LoginResponse>('/app/v1/auth/login', {
      method: 'POST',
      body: request,
      anonymous: true,
    }),

  /** Girişi authenticator kodu, kurtarma kodu ya da e-postaya gelen kodla tamamlar. */
  verifyTwoFactor: (request: VerifyTwoFactorRequest) =>
    apiRequest<SessionResponse>('/app/v1/auth/two-factor/verify', {
      method: 'POST',
      body: request,
      anonymous: true,
    }),

  /**
   * Giriş kodunu yeniden yollatır. Anonim, çünkü ait olduğu adım da öyle: yetkilendiren şey
   * challenge, ve adres istekte değil, challenge'ın adlandırdığı hesapta.
   */
  resendTwoFactorCode: (request: ResendTwoFactorCodeRequest) =>
    apiRequest<TwoFactorCodeDelivery>('/app/v1/auth/two-factor/resend', {
      method: 'POST',
      body: request,
      anonymous: true,
    }),

  logout: () => apiRequest<void>('/app/v1/auth/logout', { method: 'POST', anonymous: true }),

  /** Adres bir hesaba ait olsa da olmasa da hep başarılı döner (enumeration kuralı). */
  forgotPassword: (request: ForgotPasswordRequest) =>
    apiRequest<void>('/app/v1/auth/forgot-password', {
      method: 'POST',
      body: request,
      anonymous: true,
    }),

  /** Davet bağlantısı ile sıfırlama bağlantısının ortak ucu. */
  setPassword: (request: SetPasswordRequest) =>
    apiRequest<void>('/app/v1/auth/set-password', {
      method: 'POST',
      body: request,
      anonymous: true,
    }),

  /**
   * Oturumlu ve mevcut şifre zorunlu — bu kurtarma değil, bakım işlemidir.
   *
   * **Yeni bir oturum döndürür.** Elde duran token `mustChangePassword` bayrağını taşımaya devam
   * ederdi ve middleware, kendisinden isteneni yapmış kullanıcıyı bir token ömrü boyunca (15 dk)
   * her uçta reddederdi. Çağıran, dönen oturumu benimsemelidir.
   *
   * Yanlış mevcut şifre `INVALID_CREDENTIALS` döner; istemci onu yenilemeye çalışmaz
   * (`lib/api/client.ts`).
   */
  changePassword: (request: ChangePasswordRequest) =>
    apiRequest<SessionResponse>('/app/v1/auth/change-password', {
      method: 'POST',
      body: request,
    }),

  /**
   * Oturumdaki kullanıcının profili.
   *
   * Oturum yükünün taşımadığı alanlar buradan gelir: e-posta ve son giriş, JWT'ye kişisel veri
   * konmadığı için orada yok (`docs/spec/03-security.md §1.1`). Ayrıca token basıldıktan sonra
   * değişen 2FA durumunu görmenin tek yolu budur.
   */
  me: () => apiRequest<CurrentUser>('/app/v1/me'),
}
